import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import React from "react";
import API_ENDPOINTS from "../apiConfig";

function HistoriePage() {
  const [wblFiles, setWblFiles] = React.useState<Array<{
    id: number;
    titel: string;
    description: string;
    file_url: string | null;
  }> | null>(null);
  const [filesLoading, setFilesLoading] = React.useState(false);
  React.useEffect(() => {
    async function fetchFiles() {
      setFilesLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.files);
        if (!res.ok) throw new Error("Fehler beim Laden der Dateien");
        const data = await res.json();
        setWblFiles(data);
      } catch (err) {
        console.error("Fehler beim Laden der WBL-Dateien", err);
        setWblFiles([]);
      } finally {
        setFilesLoading(false);
      }
    }
    fetchFiles();
  }, []);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const cols = isXs ? 1 : isSm ? 2 : 3;
  const [openImg, setOpenImg] = React.useState<string | null>(null);
  const [previewFile, setPreviewFile] = React.useState<{
    url: string;
    type: "image" | "pdf";
    title: string;
  } | null>(null);

  // Helper function to determine file type
  const getFileType = (url: string): "image" | "pdf" | "other" => {
    const extension = url.toLowerCase().split(".").pop();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "")) {
      return "image";
    }
    if (extension === "pdf") {
      return "pdf";
    }
    return "other";
  };

  const historyBasePath =
    "https://s3.eu-central-1.amazonaws.com/wbl-dartliga-bucket.de/history";
  // Helper: List of all history image entries
  const historyImages = [
    {
      year: "1987-88",
      files: ["steeldart_hinrundentabelle_1987_88.jpg"],
    },
    {
      year: "1988-89",
      files: [
        "steeldart_abschlusstabelle_1988_89_1.jpg",
        "steeldart_abschlusstabelle_1988_89_2.jpg",
        "steeldart_abschlusstabelle_1988_89_3.jpg",
      ],
    },
    {
      year: "1989-90",
      files: [
        "steeldart_abschlusstabelle_1989_90_1.jpg",
        "steeldart_abschlusstabelle_1989_90_2.jpg",
      ],
    },
    { year: "1991-92", files: ["steeldart_abschlusstabelle_1991_92.jpg"] },
    {
      year: "1992-93 keine Abschlusstab",
      files: ["steeldart_keine_abschlusstabelle_1992_93.jpg"],
    },
    { year: "2002-03", files: ["steeldart_abschlusstabelle_2002_03.jpg"] },
    { year: "2007-08", files: ["steeldart_abschlusstabelle_2007_08.jpg"] },
    { year: "2008-09", files: ["steeldart_abschlusstabelle_2008_09.jpg"] },
    { year: "2015-16", files: ["steeldart_abschlusstabelle_2015_16.jpg"] },
    { year: "2016-17", files: ["steeldart_abschlusstabelle_2016_17.jpg"] },
    { year: "2017-18", files: ["steeldart_abschlusstabelle_2017_18.jpg"] },
    { year: "2018-19", files: ["FB_IMG_1747458689210.jpg"] },
    { year: "2021-22", files: ["FB_IMG_1747458428413.jpg"] },
  ];

  return (
    <>
      <Box
        sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, sm: 4 }, mt: 4, mb: 8 }}
      >
        <Typography
          variant="h2"
          sx={{ mb: 3, textAlign: "left", fontWeight: 700, color: "text.primary" }}
        >
          Württemberg-Dart-Liga Dateien & Dokumente
        </Typography>
        {filesLoading ? (
          <Typography sx={{ mb: 2 }}>Lade Dateien...</Typography>
        ) : wblFiles && wblFiles.length > 0 ? (
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 3,
              }}
            >
              {wblFiles.map((file) => (
                <Box
                  key={file.id}
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: 3,
                    background: "#fff",
                    cursor: file.file_url ? "pointer" : "default",
                    transition: "all 0.3s ease",
                    "&:hover": file.file_url
                      ? {
                          transform: "translateY(-4px)",
                          boxShadow: 6,
                        }
                      : {},
                  }}
                  onClick={() => {
                    if (
                      file.file_url &&
                      (getFileType(file.file_url) === "image" ||
                        getFileType(file.file_url) === "pdf")
                    ) {
                      setPreviewFile({
                        url: file.file_url,
                        type: getFileType(file.file_url) as "image" | "pdf",
                        title: file.titel,
                      });
                    } else if (file.file_url) {
                      // For non-previewable files, open in new tab
                      window.open(file.file_url, "_blank");
                    }
                  }}
                >
                  {/* Preview Area */}
                  <Box
                    sx={{
                      height: 200,
                      background: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {file.file_url && getFileType(file.file_url) === "image" ? (
                      <img
                        src={file.file_url}
                        alt={file.titel}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          // Fallback if image fails to load
                          e.currentTarget.style.display = "none";
                          e.currentTarget.parentElement!.innerHTML = `
                            <div style="display: flex; flex-direction: column; align-items: center; color: #666;">
                              <div style="font-size: 48px; margin-bottom: 8px;">🖼️</div>
                              <div>Bild</div>
                            </div>
                          `;
                        }}
                      />
                    ) : file.file_url &&
                      getFileType(file.file_url) === "pdf" ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          color: "#666",
                        }}
                      >
                        <Typography sx={{ fontSize: "48px", mb: 1 }}>
                          📄
                        </Typography>
                        <Typography variant="body2">
                          PDF Dokument ansehen
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          color: "#666",
                        }}
                      >
                        <Typography sx={{ fontSize: "48px", mb: 1 }}>
                          📁
                        </Typography>
                        <Typography variant="body2">Dokument</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Content Area */}
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 1,
                        lineHeight: 1.3,
                      }}
                    >
                      {file.titel}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {file.description}
                    </Typography>
                  </Box>

                  {/* Type Indicator */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {file.file_url
                      ? getFileType(file.file_url) === "image"
                        ? "BILD"
                        : getFileType(file.file_url) === "pdf"
                        ? "PDF"
                        : "DATEI"
                      : "KEIN LINK"}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Typography sx={{ mb: 2 }}>Keine Dateien gefunden.</Typography>
        )}
        <Typography
          variant="h1"
          sx={{
            mb: 2,
            fontSize: { xs: "2rem", sm: "2.5rem" },
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          Steeldart Historie & Tabellen – Dartliga Württemberg &
          Baden-Württemberg
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 4, textAlign: "center" }}>
          Alle Saisons der Steeldart Dartliga Württemberg & Baden-Württemberg
        </Typography>
        <Typography
          variant="h2"
          sx={{ fontSize: "1.3rem", mb: 2, color: "secondary.main" }}
        >
          Saison-Tabellen, Steeldart Bilder & Dokumente
        </Typography>
        <Typography sx={{ mb: 2 }}>
          Hier findest du die Abschlusstabellen, Bilder und Dokumente
          vergangener Saisons der Steeldart Dartliga Württemberg und
          Baden-Württemberg. Die Historie wird laufend ergänzt. Für vollständige
          Tabellen und Spielpläne siehe die PDF-Downloads und Bilder unten.
          Steeldart, Dart, Württemberg und Baden-Württemberg stehen im Fokus
          unseres Dartsports.
        </Typography>

        <Box sx={{ mt: 5 }}>
          <Typography
            variant="h3"
            sx={{ fontSize: "1.1rem", mb: 2, color: "text.primary" }}
          >
            Abschlusstabellen vergangener Saisons unserer Steeldart Liga in
            Baden Würrtemberg
          </Typography>
          {historyImages.map(({ year, files }) => (
            <Box key={year} sx={{ mb: 4 }}>
              <Typography
                variant="h4"
                sx={{ fontSize: "1rem", mb: 1, color: "secondary.main" }}
              >
                {year}
              </Typography>
              <ImageList
                sx={{ width: "100%", maxWidth: 900, margin: "0 auto" }}
                cols={cols}
                gap={16}
              >
                {files.map((img) => {
                  const imgUrl = `${historyBasePath}/${year}/${img}`;
                  return (
                    <ImageListItem
                      key={img}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        background: "#f2f2f2",
                        borderRadius: 2,
                        padding: 1,
                      }}
                    >
                      <Box
                        sx={{
                          height: 220,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 2,
                          overflow: "hidden",
                          mb: 1,
                          background: "#000",
                          cursor: "pointer",
                          border: "1px solid #111",
                        }}
                        onClick={() => setOpenImg(imgUrl)}
                      >
                        <Tooltip
                          title="Bild vergrößern / Vollbild anzeigen"
                          followCursor
                          placement="top"
                        >
                          <img
                            src={imgUrl}
                            alt={`Abschlusstabelle Steeldart Dartliga Württemberg & Baden-Württemberg Saison ${year
                              .replace(/[^0-9/-]+/g, "")
                              .replace(/_/g, " ")}`}
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ mt: 1, textAlign: "center", color: "#555" }}
                      >
                        {`Abschlusstabelle Steeldart Dartliga Württemberg & Baden-Württemberg Saison ${year
                          .replace(/[^0-9/-]+/g, "")
                          .replace(/_/g, " ")}`}
                      </Typography>
                      <Tooltip title="Download / Herunterladen">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{
                            minWidth: 0,
                            px: 1,
                            borderRadius: 2,
                            mt: 0.5,
                          }}
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = imgUrl;
                            link.download = img;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </ImageListItem>
                  );
                })}
              </ImageList>
            </Box>
          ))}
        </Box>
      </Box>
      <Dialog
        open={!!openImg}
        onClose={() => setOpenImg(null)}
        maxWidth="md"
        sx={{ overflowY: "hidden" }}
      >
        <DialogContent
          sx={{
            p: 0,
            background: "#111",
            position: "relative",
          }}
        >
          {openImg && (
            <>
              <img
                src={openImg}
                alt={openImg.replace(/_/g, " ").replace(/.jpg|.jpeg|.png/, "")}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "80vh",
                  display: "block",
                  margin: 0,
                  background: "#111",
                }}
              />
              <Tooltip title="Download / Herunterladen">
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 0,
                    borderRadius: 2,
                    minWidth: 0,
                    px: 1,
                  }}
                  component="a"
                  href={openImg}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <ArrowDownwardIcon fontSize="medium" />
                </Button>
              </Tooltip>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog for Images and PDFs */}
      <Dialog
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflow: "hidden", // Prevent dialog-level scrolling
          },
        }}
      >
        {previewFile && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 1,
                flexShrink: 0, // Prevent title from shrinking
              }}
            >
              <Typography variant="h6" component="div">
                {previewFile.title}
              </Typography>
              <IconButton
                onClick={() => setPreviewFile(null)}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                p: 0,
                position: "relative",
                overflow: "hidden", // Prevent content-level scrolling
                display: "flex",
                flexDirection: "column",
              }}
            >
              {previewFile.type === "image" ? (
                <img
                  src={previewFile.url}
                  alt={previewFile.title}
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "calc(90vh - 80px)", // Account for dialog title
                    display: "block",
                    margin: 0,
                    objectFit: "contain", // Ensure proper scaling
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: "calc(90vh - 80px)", // Account for dialog title
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    src={previewFile.url}
                    title={previewFile.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                </Box>
              )}
              <Tooltip title="Download / Herunterladen">
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    borderRadius: 2,
                    minWidth: 0,
                    px: 1,
                    zIndex: 1,
                  }}
                  component="a"
                  href={previewFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <ArrowDownwardIcon fontSize="medium" />
                </Button>
              </Tooltip>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}

export default HistoriePage;
